import { useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  CalendarCheck,
  CalendarPlus,
  Check,
  Info,
  MapPin,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import ModalBase from "components/ModalBase";
import InputTime from "components/CustomInputs/InputTime";
import InputWithLabel from "components/CustomInputs/InputWithLabel";
import useMask from "hooks/useMask";
import { buildFedexPickupDTO } from "services/fedex/fedex.mapper";
import { postFedexPickup } from "services/fedex/fedex.service";
import { postUpsPickup } from "services/ups/ups.service";
import { getResumoCotacoes } from "services/cotacoes/cotacoes.service";
import { AuthContext } from "context/AuthContext";
import { getClienteAtual } from "services/cliente";

type Transportadora = "fedex" | "ups";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

/** Índice do modal "Coletas agendadas" no menu lateral. */
const MODAL_COLETAS_AGENDADAS = 11;

/**
 * Mensagem de erro da API de coleta.
 *
 * As duas transportadoras respondem com chaves diferentes: o controller da
 * FedEx devolve `error` e o da UPS devolve `message`. Erros lançados antes do
 * HTTP (ex.: conta FedEx não configurada no ambiente) só têm `err.message`.
 */
const mensagemErro = (err: unknown): string => {
  const data = (err as { response?: { data?: { error?: string; message?: string } } })
    ?.response?.data;

  return (
    data?.error || data?.message || (err as Error)?.message || "Falha ao agendar coleta."
  );
};

/** "70.733-540" / "70733540" → "70733-540". Só dígitos, no formato do campo. */
const formatarCep = (valor?: string | null): string => {
  const digitos = String(valor || "").replace(/\D/g, "").slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
};

const TRANSPORTADORAS: {
  value: Transportadora;
  nome: string;
  sigla: string;
  cor: string;
}[] = [
  { value: "fedex", nome: "FedEx", sigla: "FX", cor: "#4D148C" },
  { value: "ups", nome: "UPS", sigla: "UPS", cor: "#B45309" },
];

const HORA_INICIAL_PADRAO = "08:00";
const HORA_FINAL_PADRAO = "17:00";

/** Limite do SpecialInstruction na Pickup API da UPS. */
const UPS_OBSERVACAO_MAX = 57;

const JANELAS = [
  { chave: "manha", label: "Manhã", de: "08:00", ate: "12:00" },
  { chave: "tarde", label: "Tarde", de: "13:00", ate: "17:00" },
  { chave: "dia", label: "Dia todo", de: "08:00", ate: "17:00" },
];

const MESES_CURTO = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const SEMANA_CURTA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES_LONGO = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const SEMANA_LONGA = [
  "domingo", "segunda-feira", "terça-feira", "quarta-feira",
  "quinta-feira", "sexta-feira", "sábado",
];

/**
 * Próximos dias úteis a partir de amanhã.
 *
 * Começa amanhã porque coleta para hoje depende da hora em que se agenda e da
 * janela que ainda resta — regra que varia por transportadora e que o sistema
 * não tem como conferir aqui.
 */
const proximosDiasUteis = (quantidade: number): Date[] => {
  const dias: Date[] = [];
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  while (dias.length < quantidade) {
    cursor.setDate(cursor.getDate() + 1);
    const semana = cursor.getDay();
    if (semana !== 0 && semana !== 6) dias.push(new Date(cursor));
  }
  return dias;
};

const ehAmanha = (data: Date) => {
  const amanha = new Date();
  amanha.setDate(amanha.getDate() + 1);
  return (
    data.getDate() === amanha.getDate() &&
    data.getMonth() === amanha.getMonth() &&
    data.getFullYear() === amanha.getFullYear()
  );
};

/** "2026-10-23" no fuso local — `toISOString` devolveria o dia anterior. */
const paraISO = (data: Date) =>
  `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(
    data.getDate(),
  ).padStart(2, "0")}`;

/* ------------------------------------------------------------------ */

const Secao = ({ titulo, extra, children }: { titulo: string; extra?: ReactNode; children: ReactNode }) => (
  <section>
    <div className="mb-2 flex items-baseline justify-between">
      <h3 className="text-xs font-semibold text-slate-600">{titulo}</h3>
      {extra}
    </div>
    {children}
  </section>
);

/**
 * Agendamento de coleta, com a transportadora escolhida aqui dentro.
 *
 * Substitui ModalColetaFedex e ModalColetaUps, que eram o mesmo formulário
 * duplicado — só o envio muda: a FedEx precisa do DTO montado pelo mapper, a
 * UPS recebe os campos direto.
 *
 * Não há seleção de envios: a coleta é agendada por endereço e janela de
 * horário, e o motorista leva o que estiver pronto. Nem a FedEx nem a UPS
 * recebem lista de etiquetas no agendamento.
 */
const ModalAgendarColeta = ({ show, title, setShowNumber, logo }: Props) => {
  const { t } = useTranslation("dashboard");

  const [transportadora, setTransportadora] = useState<Transportadora>("fedex");
  const [date, setDate] = useState<Date>();
  const [start, setStart] = useState(HORA_INICIAL_PADRAO);
  const [end, setEnd] = useState(HORA_FINAL_PADRAO);

  const [country, setCountry] = useState("BR");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [cep, setCEP] = useState("");
  const [rua, setRua] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [observacao, setObservacao] = useState("");

  const [enderecoFixo, setEnderecoFixo] = useState(false);
  const [editandoEndereco, setEditandoEndereco] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [agendada, setAgendada] = useState<{ confirmacao: string | null } | null>(null);
  // Envios com etiqueta emitida e ainda não coletados, por transportadora.
  const [aguardando, setAguardando] = useState<Record<Transportadora, number | null>>({
    fedex: null,
    ups: null,
  });

  const auth = useContext(AuthContext);
  const maskCep = useMask(["00000-000", "00000-0000"]);
  const maskTelefone = useMask(["(00) 0000-0000", "(00) 00000-0000"]);

  const dias = useMemo(() => proximosDiasUteis(5), []);

  const cepDigitos = cep.replace(/\D/g, "");
  const ehBrasil = country.trim().toUpperCase() === "BR";
  const cepInvalido = cep.trim() !== "" && ehBrasil && cepDigitos.length !== 8;
  const estadoNormalizado = state.trim().toUpperCase();
  const paisNormalizado = country.trim().toUpperCase();

  useEffect(() => {
    getClienteAtual().then((res) => {
      setTelefone(res.telefoneCelular);
      if (res.remetenteFixoAtivo) {
        setEnderecoFixo(true);
        setCountry(res.enderecoPais);
        setState(res.enderecoEstado);
        setCity(res.enderecoCidade);
        setCEP(formatarCep(res.enderecoCEP));
        setRua(res.enderecoRua);
        setNumero(res.enderecoNumero);
        setComplemento(res.enderecoComplemento || "");
      } else {
        // Sem remetente fixo não há o que mostrar em modo leitura.
        setEditandoEndereco(true);
      }
    });
  }, []);

  // Quantos envios estão esperando coleta em cada transportadora. Falha
  // sozinho: é informação de apoio, não pode impedir o agendamento.
  useEffect(() => {
    if (!show) return;
    TRANSPORTADORAS.forEach((op) => {
      getResumoCotacoes({ carrier: op.value.toUpperCase() })
        .then((r) => setAguardando((a) => ({ ...a, [op.value]: r.por_status.CRIADO ?? 0 })))
        .catch(() => undefined);
    });
  }, [show]);

  useEffect(() => {
    if (show) return;
    setAgendada(null);
    setDate(undefined);
    setObservacao("");
  }, [show]);

  const pendencias = useMemo(() => {
    const faltando: string[] = [];
    if (!date) faltando.push("o dia da coleta");
    if (!start || !end) faltando.push("a janela de horário");
    if (start && end && end <= start) faltando.push("um horário final depois do inicial");
    if (!cep.trim()) faltando.push("o CEP");
    else if (cepInvalido) faltando.push(`CEP com 8 dígitos (este tem ${cepDigitos.length})`);
    if (!rua.trim()) faltando.push("a rua");
    if (!numero.trim()) faltando.push("o número");
    if (!city.trim()) faltando.push("a cidade");
    if (!estadoNormalizado) faltando.push("o estado");
    if (!paisNormalizado) faltando.push("o país");
    if (!telefone.trim()) faltando.push("o telefone");
    return faltando;
  }, [date, start, end, cep, cepInvalido, cepDigitos.length, rua, numero, city, estadoNormalizado, paisNormalizado, telefone]);

  const isFormValid = pendencias.length === 0;
  const nomeCarrier = transportadora === "fedex" ? "FedEx" : "UPS";

  const handleSubmit = async (): Promise<{ ok: boolean; erro?: string; confirmacao?: string | null }> => {
    if (!auth?.user?.razaoSocial) {
      return { ok: false, erro: "Não foi possível identificar sua empresa. Recarregue a página." };
    }
    const personName = auth.user.razaoSocial;

    try {
      if (transportadora === "fedex") {
        const dto = buildFedexPickupDTO({
          date,
          start,
          end,
          country: paisNormalizado,
          state: estadoNormalizado,
          city,
          cep: cepDigitos,
          rua,
          numero,
          complemento,
          telefone,
          personName,
        });

        const result = await postFedexPickup(dto);
        return result?.ok
          ? {
              ok: true,
              confirmacao:
                result?.raw?.output?.pickupConfirmationCode ??
                result?.raw?.pickupConfirmationCode ??
                null,
            }
          : { ok: false, erro: result?.error || "A FedEx recusou o agendamento." };
      }

      const result = await postUpsPickup({
        pickupDate: date ? paraISO(date) : "",
        readyTime: start.slice(0, 5),
        closeTime: end.slice(0, 5),
        countryCode: paisNormalizado,
        stateCode: estadoNormalizado,
        city,
        postalCode: cepDigitos,
        addressLine1: `${rua}, ${numero}${complemento ? ` - ${complemento}` : ""}`,
        contactName: personName,
        phone: telefone.replace(/\D/g, ""),
        packageCount: 1,
        totalWeight: 1,
        serviceCode: "012",
        specialInstructions: observacao.trim() || undefined,
      });

      return result?.ok
        ? {
            ok: true,
            // O controller devolve { ok, message, data }, com a resposta da
            // UPS dentro de `data` — é de lá que ele mesmo tira o
            // PickupNumber para gravar em coletas_agendadas.
            confirmacao: result?.data?.PickupCreationResponse?.PickupNumber ?? null,
          }
        : { ok: false, erro: result?.message || "A UPS recusou o agendamento." };
    } catch (err) {
      console.error(err);
      return { ok: false, erro: mensagemErro(err) };
    }
  };

  const agendar = async () => {
    setEnviando(true);
    const result = await handleSubmit();
    setEnviando(false);

    if (result.ok) {
      setAgendada({ confirmacao: result.confirmacao ?? null });
    } else {
      // Mantém o modal aberto no erro: fechar apagava os dados que o cliente
      // acabou de digitar e obrigava a preencher tudo de novo.
      toast.error("Falha ao agendar coleta", { description: result.erro, duration: 8000 });
    }
  };

  const enderecoLinha1 = [rua, numero && `nº ${numero}`, complemento]
    .filter(Boolean)
    .join(", ");
  const enderecoLinha2 = [city && `${city}, ${estadoNormalizado}`, cep && `CEP ${cep}`, paisNormalizado]
    .filter(Boolean)
    .join("   ");

  /* ----------------------------- sucesso ---------------------------- */

  if (agendada) {
    return (
      <ModalBase
        show={show}
        onClose={() => setShowNumber(-1)}
        titulo={title}
        icone={logo ?? <Truck />}
        subtitulo="A transportadora busca as caixas no seu endereço"
        largura="640px"
      >
        <Sucesso
          carrier={nomeCarrier}
          data={date}
          de={start}
          ate={end}
          confirmacao={agendada.confirmacao}
          endereco={`${enderecoLinha1} · ${city}, ${estadoNormalizado}`}
          onVerColetas={() => setShowNumber(MODAL_COLETAS_AGENDADAS)}
        />
      </ModalBase>
    );
  }

  /* --------------------------- formulário --------------------------- */

  return (
    <ModalBase
      show={show}
      onClose={() => setShowNumber(-1)}
      titulo={title}
      icone={logo ?? <Truck />}
      subtitulo="A transportadora busca as caixas no seu endereço"
      largura="940px"
      corpoSemPadding
      rodape={
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-white px-6 py-4">
          <button
            type="button"
            onClick={() => setShowNumber(-1)}
            className="text-sm text-slate-500 transition-colors hover:text-slate-700"
          >
            {t("buttons.cancel")}
          </button>

          <div className="flex items-center gap-3">
            {!isFormValid && !enviando && (
              <span className="text-right text-xs text-amber-600">
                Falta {pendencias[0]}
                {pendencias.length > 1 ? ` e mais ${pendencias.length - 1}` : ""}
              </span>
            )}
            <button
              type="button"
              disabled={!isFormValid || enviando}
              onClick={agendar}
              className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CalendarCheck className="h-4 w-4" />
              {enviando ? "Agendando…" : `Agendar coleta ${isFormValid ? nomeCarrier : ""}`.trim()}
            </button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px]">
        {/* COLUNA DO FORMULÁRIO */}
        <div className="space-y-5 px-6 py-5">
          <Secao titulo="Transportadora">
            <div className="grid grid-cols-2 gap-3">
              {TRANSPORTADORAS.map((op) => {
                const ativa = transportadora === op.value;
                const n = aguardando[op.value];
                return (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => setTransportadora(op.value)}
                    aria-pressed={ativa}
                    className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                      ativa ? "border-brand-sidebar bg-slate-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <span
                      className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${
                        ativa ? "border-brand-sidebar" : "border-slate-300"
                      }`}
                    >
                      {ativa && <span className="h-2 w-2 rounded-full bg-brand-sidebar" />}
                    </span>
                    <span
                      className="rounded px-1.5 py-1 text-[10px] font-bold text-white"
                      style={{ backgroundColor: op.cor }}
                    >
                      {op.sigla}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{op.nome}</span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {n === null
                          ? "—"
                          : `${n} ${n === 1 ? "envio aguardando" : "envios aguardando"} coleta`}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Secao>

          <Secao
            titulo="Dia da coleta"
            extra={<span className="text-[11px] text-slate-400">Somente dias úteis</span>}
          >
            <div className="grid grid-cols-5 gap-2">
              {dias.map((dia) => {
                const ativa = date ? paraISO(date) === paraISO(dia) : false;
                return (
                  <button
                    key={paraISO(dia)}
                    type="button"
                    onClick={() => setDate(dia)}
                    className={`flex flex-col items-center gap-0.5 rounded-xl border py-2.5 transition-colors ${
                      ativa
                        ? "border-brand-sidebar bg-brand-sidebar text-white"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <span className={`text-[10px] ${ativa ? "opacity-80" : "text-slate-400"}`}>
                      {ehAmanha(dia) ? "Amanhã" : SEMANA_CURTA[dia.getDay()]}
                    </span>
                    <span className="text-lg font-bold leading-none">{dia.getDate()}</span>
                    <span className={`text-[10px] ${ativa ? "opacity-80" : "text-slate-400"}`}>
                      {MESES_CURTO[dia.getMonth()]}
                    </span>
                  </button>
                );
              })}
            </div>
          </Secao>

          <Secao titulo="Janela de horário">
            <div className="flex flex-wrap items-center gap-2">
              {JANELAS.map((j) => {
                const ativa = start === j.de && end === j.ate;
                return (
                  <button
                    key={j.chave}
                    type="button"
                    onClick={() => {
                      setStart(j.de);
                      setEnd(j.ate);
                    }}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      ativa
                        ? "border-brand-primary bg-blue-50 text-brand-primary"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {j.label} · {j.de.replace(":", "h")}–{j.ate.replace(":", "h")}
                  </button>
                );
              })}

              <div className="ml-auto flex items-center gap-2">
                <InputTime label="Das" setTime={setStart} time={start} />
                <InputTime label="até" setTime={setEnd} time={end} />
              </div>
            </div>
            {end <= start && (
              <p className="mt-2 text-xs text-red-600">
                O horário final precisa ser depois do inicial.
              </p>
            )}
          </Secao>

          <Secao titulo="Endereço de coleta">
            {!editandoEndereco ? (
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-brand-primary">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate text-sm font-medium text-slate-800">
                    {enderecoLinha1 || "Endereço não preenchido"}
                    {enderecoFixo && (
                      <span className="shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                        Principal
                      </span>
                    )}
                  </p>
                  <p className="truncate text-[11px] text-slate-400">
                    {enderecoLinha2}
                    {telefone ? `   ${telefone}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditandoEndereco(true)}
                  className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Alterar
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_2.4fr_0.8fr] gap-3">
                  <div>
                    <InputWithLabel
                      label={t("fields.zip")}
                      setter={setCEP}
                      value={cep}
                      placeholder="00000-000"
                      mask={maskCep}
                      aria-invalid={cepInvalido}
                    />
                    {cepInvalido && (
                      <p className="mt-1 px-1 text-xs text-red-600">
                        {cepDigitos.length} de 8 dígitos
                      </p>
                    )}
                  </div>
                  <InputWithLabel
                    label={t("fields.street")}
                    setter={setRua}
                    value={rua}
                    placeholder="Rua, avenida..."
                  />
                  <InputWithLabel
                    label={t("fields.number")}
                    setter={setNumero}
                    value={numero}
                    placeholder="123"
                  />
                </div>

                <div className="grid grid-cols-[2.2fr_0.7fr_0.7fr] gap-3">
                  <InputWithLabel
                    label={t("fields.city")}
                    setter={setCity}
                    value={city}
                    placeholder="Cidade"
                  />
                  <InputWithLabel
                    label={t("fields.state")}
                    setter={(v) => setState(v.toUpperCase())}
                    value={state}
                    placeholder="UF"
                    maxLength={2}
                  />
                  <InputWithLabel
                    label={t("fields.country")}
                    setter={(v) => setCountry(v.toUpperCase())}
                    value={country}
                    placeholder="BR"
                    maxLength={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <InputWithLabel
                    label={`${t("fields.complement")} (opcional)`}
                    setter={setComplemento}
                    value={complemento}
                    placeholder="Sala, andar..."
                  />
                  <InputWithLabel
                    label={t("fields.phone")}
                    setter={setTelefone}
                    value={telefone}
                    placeholder="(00) 00000-0000"
                    mask={maskTelefone}
                  />
                </div>

                {enderecoFixo && (
                  <button
                    type="button"
                    onClick={() => setEditandoEndereco(false)}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Usar o endereço principal
                  </button>
                )}
              </div>
            )}
          </Secao>

          {/* Só UPS: o campo SpecialInstruction existe na Pickup API dela e
              não tem equivalente no agendamento da FedEx. */}
          {transportadora === "ups" && (
            <Secao titulo="Instrução para o motorista (opcional)">
              <div className="relative">
                <input
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value.slice(0, UPS_OBSERVACAO_MAX))}
                  maxLength={UPS_OBSERVACAO_MAX}
                  placeholder="Ex.: retirar na portaria, procurar João"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 pr-14 text-sm outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                  {observacao.length}/{UPS_OBSERVACAO_MAX}
                </span>
              </div>
            </Secao>
          )}
        </div>

        {/* COLUNA DE APOIO */}
        <aside className="space-y-4 border-slate-100 bg-slate-50/60 px-5 py-5 md:border-l">
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Resumo
            </p>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-sm font-semibold text-slate-900">
                {nomeCarrier} ·{" "}
                {date ? (
                  <span className="font-normal text-slate-600">
                    {ehAmanha(date) ? "Amanhã, " : ""}
                    {date.getDate()} {MESES_CURTO[date.getMonth()]}
                  </span>
                ) : (
                  <span className="font-normal text-amber-600">escolha o dia</span>
                )}
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                {start} – {end}
                {enderecoLinha1 ? ` · ${enderecoLinha1}` : ""}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Antes do motorista chegar
            </p>
            <ul className="space-y-2">
              {[
                "Caixas lacradas, com etiqueta e invoice coladas",
                "Alguém no endereço durante toda a janela",
              ].map((item) => (
                <li key={item} className="flex gap-2 text-[11px] leading-snug text-slate-600">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="flex gap-2 text-[11px] leading-snug text-slate-400">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />O motorista leva o que estiver
            pronto no endereço — não é preciso escolher envios.
          </p>
        </aside>
      </div>
    </ModalBase>
  );
};

export default ModalAgendarColeta;

/* ------------------------------------------------------------------ */
/* Sucesso                                                             */
/* ------------------------------------------------------------------ */

const Sucesso = ({
  carrier,
  data,
  de,
  ate,
  confirmacao,
  endereco,
  onVerColetas,
}: {
  carrier: string;
  data?: Date;
  de: string;
  ate: string;
  confirmacao: string | null;
  endereco: string;
  onVerColetas: () => void;
}) => {
  const quando = data
    ? `${SEMANA_LONGA[data.getDay()]}, ${data.getDate()} de ${MESES_LONGO[data.getMonth()]}`
    : "a data escolhida";

  /**
   * Arquivo .ics para o calendário do cliente.
   *
   * Horário "flutuante" (sem Z e sem TZID): o agendamento é no fuso de quem
   * recebe a coleta, e converter para UTC aqui erraria a hora para quem abre
   * o convite em outro fuso.
   */
  const baixarIcs = () => {
    if (!data) return;
    const dia = `${data.getFullYear()}${String(data.getMonth() + 1).padStart(2, "0")}${String(
      data.getDate(),
    ).padStart(2, "0")}`;
    const hora = (h: string) => `${h.replace(":", "")}00`;

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Intrex//Coleta//PT",
      "BEGIN:VEVENT",
      `UID:coleta-${dia}-${Date.now()}@intrex`,
      `DTSTART:${dia}T${hora(de)}`,
      `DTEND:${dia}T${hora(ate)}`,
      `SUMMARY:Coleta ${carrier}`,
      `DESCRIPTION:Confirmação: ${confirmacao || "—"}`,
      `LOCATION:${endereco.replace(/,/g, "\\,")}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `coleta-${carrier.toLowerCase()}-${dia}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col items-center px-6 py-8 text-center">
      <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white">
        <Check className="h-7 w-7" strokeWidth={3} />
      </div>

      <h3 className="text-lg font-semibold text-slate-900">Coleta agendada</h3>
      <p className="mt-1 max-w-[46ch] text-xs leading-relaxed text-slate-500">
        A {carrier} vai buscar suas caixas em {quando}, {de} – {ate}.
      </p>

      <div className="mt-5 grid w-full max-w-md grid-cols-2 gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Confirmação</p>
          <p className="truncate font-mono text-xs font-semibold text-slate-800">
            {confirmacao || "—"}
          </p>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Endereço</p>
          <p className="truncate text-xs font-medium text-slate-800">{endereco}</p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={baixarIcs}
          disabled={!data}
          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
        >
          <CalendarPlus className="mr-1.5 inline h-3.5 w-3.5" />
          Adicionar à agenda
        </button>
        <button
          onClick={onVerColetas}
          className="rounded-lg bg-brand-sidebar px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        >
          Ver coletas agendadas
        </button>
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        Avisamos por e-mail se a transportadora mudar o horário.
      </p>
    </div>
  );
};
